import {
  Box,
  Accordion as ChakraAccordion,
  HStack,
  Stack,
} from "@chakra-ui/react";
import * as React from "react";
import { LuChevronDown } from "react-icons/lu";

interface AccordionItemTriggerProps extends ChakraAccordion.ItemTriggerProps {
  indicatorPlacement?: "start" | "end";
}

export const AccordionItemTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionItemTriggerProps
>(function AccordionItemTrigger(props, ref) {
  const { children, indicatorPlacement = "end", ...rest } = props;
  return (
    <ChakraAccordion.ItemTrigger {...rest} ref={ref}>
      {indicatorPlacement === "start" && (
        <ChakraAccordion.ItemIndicator
          rotate={{ base: "-90deg", _open: "0deg" }}
        >
          <LuChevronDown />
        </ChakraAccordion.ItemIndicator>
      )}
      <HStack gap="4" flex="1" textAlign="start" width="full">
        {children}
      </HStack>
      {indicatorPlacement === "end" && (
        <ChakraAccordion.ItemIndicator>
          <LuChevronDown />
        </ChakraAccordion.ItemIndicator>
      )}
    </ChakraAccordion.ItemTrigger>
  );
});

interface AccordionItemContentProps extends ChakraAccordion.ItemContentProps {}

export const AccordionItemContent = React.forwardRef<
  HTMLDivElement,
  AccordionItemContentProps
>(function AccordionItemContent(props, ref) {
  return (
    <ChakraAccordion.ItemContent>
      <ChakraAccordion.ItemBody {...props} ref={ref} />
    </ChakraAccordion.ItemContent>
  );
});
export const AccordionRoot = ChakraAccordion.Root;
export const AccordionItem = ChakraAccordion.Item;

export interface AccordionContentProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export const AccordionContent: React.FC<AccordionContentProps> = ({
  children,
  disabled = false,
}) => null;

interface AccordionProps {
  items: { label: any; value: string }[];
  defaultValue?: string[];
  children?: React.ReactNode;
  disabled?: boolean;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  function Accordion(props, ref): React.ReactElement {
    const { items, defaultValue, children, disabled = false } = props;
    const [value, setValue] = React.useState(defaultValue || [items[0].value]);

    const contentArray = React.Children.toArray(
      children
    ) as React.ReactElement<AccordionContentProps>[];

    return (
      <Stack gap="5" ref={ref}>
        <ChakraAccordion.Root
          multiple
          value={value}
          onValueChange={(e) => setValue(e.value)}
          disabled={disabled}
        >
          {items.map((item) => {
            const contentChild = contentArray.find(
              (child) => child.props.value === item.value
            );
            const isItemDisabled = disabled || contentChild?.props.disabled;

            return (
              <ChakraAccordion.Item
                disabled={isItemDisabled}
                key={item.value}
                value={item.value}
              >
                <AccordionItemTrigger>{item.label}</AccordionItemTrigger>

                {contentChild && !isItemDisabled && (
                  <AccordionItemContent key={item.value}>
                    <Box>{contentChild.props.children}</Box>
                  </AccordionItemContent>
                )}
              </ChakraAccordion.Item>
            );
          })}
        </ChakraAccordion.Root>
      </Stack>
    );
  }
);
